import { Link } from 'react-router';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LabelChip, labelColorProps } from '../labels/LabelChip';
import { Icon } from '../Icon';
import { formatDate } from '../../lib/format';
import { Avatar } from '../Avatar';
import { shortWeek, type BoardCardData, type ColumnKey } from './boardModel';

/** How a card shows its labels: short coloured bars (default) or named solid chips. */
export type LabelMode = 'compact' | 'full';

/** Avatars shown on a card before the rest fold into "+N". */
const MAX_AVATARS = 3;

/** Up to three assignee avatars, then "+N" for the rest (the title lists everyone). */
function Assignees({ emails }: { emails: string[] }) {
  if (emails.length === 0) return null;
  const shown = emails.slice(0, MAX_AVATARS);
  const rest = emails.length - shown.length;
  return (
    <span
      className="req-avstack board-card__people"
      title={`Assigned: ${emails.join(', ')}`}
      aria-label={`Assigned: ${emails.join(', ')}`}
      data-testid="board-card-assignees"
    >
      {shown.map((e) => (
        <Avatar key={e} email={e} />
      ))}
      {rest > 0 && (
        <span className="req-av board-card__more" aria-hidden="true">
          +{rest}
        </span>
      )}
    </span>
  );
}

/**
 * A board card: title + #id, the non-workflow labels, then due, the access grant's
 * state, "since Wnn" when the card was planned in an earlier week, and up to three assignees. The
 * whole card is the drag handle (mouse after 5px, touch after a 250ms press, keyboard with
 * Space/Enter), so a plain click on the title still opens the task. Dragging it up or down sets
 * its place in the column.
 */
export function BoardCardBody({
  card,
  now,
  labelMode = 'compact',
}: {
  card: BoardCardData;
  now: number;
  labelMode?: LabelMode;
}) {
  const due = card.dueAt ? new Date(card.dueAt).getTime() : null;
  const overdue = due != null && due < now && card.state === 'open';
  const labels = card.labels.filter((l) => l.scope !== 'workflow');
  return (
    <>
      <div className="board-card__head">
        <Link to={`/tasks/${card.id}`} className="board-card__title" draggable={false} title={card.title}>
          {card.title}
        </Link>
        <span className="board-card__id">#{card.id}</span>
      </div>
      {labels.length > 0 && labelMode === 'compact' && (
        // Trello-style: one coloured bar per label, the names in each bar's title and in the
        // group's accessible name. The toolbar's "Label names" button switches to named chips.
        <div
          className="board-card__labels board-card__labels--compact"
          role="img"
          aria-label={`Labels: ${labels.map((l) => l.name).join(', ')}`}
          data-testid="board-card-labels"
          data-mode="compact"
        >
          {labels.map((l) => {
            const c = labelColorProps(l.color);
            return (
              <span
                key={l.id}
                className={`board-label-bar ${c.className}`}
                style={c.style}
                title={l.name}
                data-testid="board-label-bar"
              />
            );
          })}
        </div>
      )}
      {labels.length > 0 && labelMode === 'full' && (
        <div className="board-card__labels" data-testid="board-card-labels" data-mode="full">
          {labels.map((l) => (
            <LabelChip
              key={l.id}
              size="lg"
              label={{ id: String(l.id), name: l.name, color: l.color, scope: l.scope, system: l.system }}
            />
          ))}
        </div>
      )}
      <div className="board-card__meta">
        {card.dueAt && (
          <span
            className={overdue ? 'req-chip req-chip--due' : 'req-chip'}
            title={overdue ? 'Overdue' : 'Due date'}
          >
            <Icon name="clock" size={12} className="req-ico" />
            {formatDate(card.dueAt)}
          </span>
        )}
        {card.access?.status === 'pending' && (
          <span className="req-chip req-chip--warn" title="The access grant is waiting for a decision">
            grant pending
          </span>
        )}
        {card.carried && card.since && (
          <span className="req-chip board-since" title={`On the board since ${card.since}`} data-testid="board-since">
            since {shortWeek(card.since)}
          </span>
        )}
        <Assignees emails={card.assignees.map((a) => a.email)} />
      </div>
    </>
  );
}

export function BoardCard({
  card,
  column,
  now,
  pending,
  readOnly = false,
  drop,
  labelMode = 'compact',
}: {
  card: BoardCardData;
  column: ColumnKey;
  now: number;
  pending: boolean;
  /** Past weeks are read-only: the card renders but cannot be picked up. */
  readOnly?: boolean;
  /** A card dragged in from another column will land just before / after this one. */
  drop?: 'before' | 'after';
  labelMode?: LabelMode;
}) {
  // A sortable item: a drag source AND a drop target (`column` tells the collision which column
  // it belongs to). In its own column the other cards slide to open a slot for it.
  const { attributes, listeners, setNodeRef, isDragging, transform, transition } = useSortable({
    id: `card-${card.id}`,
    data: { card, from: column, column },
    disabled: readOnly,
    // No post-drop layout animation: right after a drop the optimistic list re-renders, and the
    // derived "slide from the old spot" transform made the next card overlap the pending one
    // until the refetch. The slide while sorting (other cards opening the slot) is unaffected.
    animateLayoutChanges: () => false,
  });
  const cls = ['board-card'];
  if (isDragging) cls.push('board-card--ghost');
  if (pending) cls.push('board-card--pending');
  return (
    <li
      ref={setNodeRef}
      className={cls.join(' ')}
      data-testid="board-card"
      data-id={card.id}
      data-column={column}
      {...(readOnly ? {} : attributes)}
      {...(readOnly ? {} : listeners)}
      data-readonly={readOnly ? 'true' : undefined}
      data-drop={drop}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      aria-roledescription={readOnly ? undefined : 'draggable task'}
      aria-label={`#${card.id} ${card.title}`}
    >
      <BoardCardBody card={card} now={now} labelMode={labelMode} />
    </li>
  );
}
